import type { DockingRecord, Maturity } from '../types/docking';

const DATA_URL = `${process.env.PUBLIC_URL || ''}/data/dockingRecords.json`;
const ATTACH_PREFIX = `${process.env.PUBLIC_URL || ''}/attachments`;

function toStringField(v: unknown): string {
  if (v == null) return '';
  return String(v).trim();
}

function toStringList(v: unknown): string[] {
  if (Array.isArray(v)) {
    return v.map((x) => toStringField(x)).filter(Boolean);
  }
  const s = toStringField(v);
  if (!s) return [];
  return s
    .split(/[,，、;；]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

function toMaturity(v: unknown): Maturity | null {
  if (typeof v === 'number' && v >= 1 && v <= 5) {
    return Math.round(v) as Maturity;
  }
  const s = toStringField(v);
  const m = s.match(/([1-5])/);
  if (!m) return null;
  return Number(m[1]) as Maturity;
}

function toCompletionStatus(
  v: unknown,
): DockingRecord['completionStatus'] {
  const s = toStringField(v);
  if (!s) return null;
  if (s === 'completed' || s === '已完成' || s === '完成') return 'completed';
  if (s === 'incomplete' || s === '未完成') return 'incomplete';
  return null;
}

function toOverdueDays(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) {
    return Math.trunc(v);
  }
  const s = toStringField(v);
  if (!s) return null;
  if (s === '未逾期' || s === '未超期' || s === '无逾期') return 0;
  const m = s.match(/-?\d+/);
  if (!m) return null;
  return Number(m[0]);
}

function memoPath(fileName: string): string | null {
  if (!fileName) return null;
  return `${ATTACH_PREFIX}/${encodeURIComponent(fileName)}`;
}

function normalizeRecord(raw: unknown, index: number): DockingRecord | null {
  const r = raw as Record<string, unknown>;
  const projectId = toStringField(r.projectId);
  if (!projectId) return null;
  const memoFileName = toStringField(r.memoFileName);
  const overdueLabel = toStringField(r.overdueLabel);
  let overdueDays = toOverdueDays(r.overdueDays);
  if (overdueDays == null && overdueLabel) {
    overdueDays = toOverdueDays(overdueLabel);
  }
  return {
    id: toStringField(r.id) || `dk-${index + 1}`,
    projectId,
    projectName: toStringField(r.projectName),
    dockedAt: toStringField(r.dockedAt),
    filedAt: toStringField(r.filedAt),
    ourParticipants: toStringList(r.ourParticipants),
    counterpartyOrg: toStringField(r.counterpartyOrg),
    counterpartyParticipants: toStringList(r.counterpartyParticipants),
    summary: toStringField(r.summary),
    maturity: toMaturity(r.maturity),
    issues: toStringField(r.issues),
    followUpPlan: toStringField(r.followUpPlan),
    plannedCompletedAt: toStringField(r.plannedCompletedAt),
    completionStatus: toCompletionStatus(r.completionStatus),
    overdueDays,
    overdueLabel,
    memoFileName,
    memoPath: memoPath(memoFileName),
  };
}

export async function fetchDockingRecords(): Promise<DockingRecord[]> {
  const res = await fetch(DATA_URL);
  if (!res.ok) {
    throw new Error(`加载对接记录失败: ${res.status}`);
  }
  const data: unknown = await res.json();
  if (!Array.isArray(data)) {
    throw new Error('对接记录数据格式错误');
  }
  return data
    .map((row, i) => normalizeRecord(row, i))
    .filter((row): row is DockingRecord => row != null);
}
